(() => {
  const SELECTOR_ROOT = "[data-course-landing]";

  function pad2(n) {
    return String(n).padStart(2, "0");
  }

  function formatTime(seconds) {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}:${pad2(ss)}`;
  }

  function safeJsonParse(value) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  }

  function initCourseLanding(root) {
    if (!root || root.dataset.init === "true") return;
    root.dataset.init = "true";

    if (root.dataset.authorized !== "true") return;

    const shopDomain = root.dataset.shopDomain || "";
    const pageHandle = root.dataset.pageHandle || "";
    const sectionId = root.dataset.sectionId || "";

    const lessonEls = Array.from(root.querySelectorAll(".course-landing__lesson"));
    if (!lessonEls.length) return;

    const stateByLesson = new Map();

    function storageKey(lessonKey) {
      return `courseProgress:${shopDomain}:${pageHandle}:${sectionId}:${lessonKey}`;
    }

    function loadProgress(lessonKey) {
      const raw = window.localStorage.getItem(storageKey(lessonKey));
      const data = safeJsonParse(raw);
      if (!data || typeof data !== "object") {
        return {
          currentTime: 0,
          duration: 0,
          seen: false,
          updatedAt: 0,
        };
      }
      return {
        currentTime: Number(data.currentTime) || 0,
        duration: Number(data.duration) || 0,
        seen: Boolean(data.seen),
        updatedAt: Number(data.updatedAt) || 0,
      };
    }

    function saveProgress(lessonKey, next) {
      window.localStorage.setItem(storageKey(lessonKey), JSON.stringify(next));
    }

    function updateHeaderUI(lessonEl, progress) {
      const badgeEl = lessonEl.querySelector("[data-badge]");
      const resumeEl = lessonEl.querySelector("[data-resume]");

      lessonEl.classList.toggle("is-seen", Boolean(progress.seen));

      if (badgeEl) {
        badgeEl.textContent = progress.seen ? "VIZIONAT" : "NEVIZIONAT";
        badgeEl.classList.toggle("is-seen", progress.seen);
      }

      if (resumeEl) {
        if (!progress.seen && progress.currentTime > 10) {
          resumeEl.textContent = `Resume at ${formatTime(progress.currentTime)}`;
        } else {
          resumeEl.textContent = "";
        }
      }
    }

    function pauseAllExcept(exceptVideo) {
      for (const { video } of stateByLesson.values()) {
        if (video && video !== exceptVideo && !video.paused) {
          video.pause();
        }
      }
    }

    function stopSavingTimer(lessonKey) {
      const st = stateByLesson.get(lessonKey);
      if (!st || !st.saveIntervalId) return;
      window.clearInterval(st.saveIntervalId);
      st.saveIntervalId = null;
    }

    function startSavingTimer(lessonKey) {
      const st = stateByLesson.get(lessonKey);
      if (!st || st.saveIntervalId) return;

      st.saveIntervalId = window.setInterval(() => {
        persistFromVideo(lessonKey, { reason: "interval" });
      }, 5000);
    }

    function persistFromVideo(lessonKey, meta) {
      const st = stateByLesson.get(lessonKey);
      if (!st || !st.video) return;

      const video = st.video;
      const duration = Number(video.duration) || st.progress.duration || 0;
      const currentTime = Number(video.currentTime) || 0;
      const watchedRatio = duration > 0 ? currentTime / duration : 0;

      const seen = Boolean(st.progress.seen || video.ended || watchedRatio >= 0.9);

      st.progress = {
        currentTime,
        duration,
        seen,
        updatedAt: Date.now(),
      };

      saveProgress(lessonKey, st.progress);
      updateHeaderUI(st.lessonEl, st.progress);

      if (seen) {
        stopSavingTimer(lessonKey);
      }
    }

    function tryResumePlayback(lessonKey) {
      const st = stateByLesson.get(lessonKey);
      if (!st || !st.video) return;

      const video = st.video;
      const progress = st.progress;
      const duration = Number(video.duration) || progress.duration || 0;
      const safeDuration = duration > 0 ? duration : 0;

      if (!safeDuration) return;
      if (progress.seen) return;
      if (!(progress.currentTime > 0)) return;

      const maxSeek = safeDuration * 0.95;
      if (progress.currentTime < maxSeek) {
        try {
          video.currentTime = progress.currentTime;
        } catch (e) {}
      }
    }

    for (const lessonEl of lessonEls) {
      const lessonKey = lessonEl.dataset.lessonKey || "";
      if (!lessonKey) continue;

      const video = lessonEl.querySelector("video[data-video]");
      if (!video) continue;

      const progress = loadProgress(lessonKey);
      updateHeaderUI(lessonEl, progress);

      const st = {
        lessonEl,
        lessonKey,
        video,
        progress,
        saveIntervalId: null,
      };
      stateByLesson.set(lessonKey, st);

      // Note: controlsList/disablePictureInPicture only discourages downloading.
      video.addEventListener("play", () => {
        pauseAllExcept(video);
        startSavingTimer(lessonKey);
      });

      video.addEventListener("pause", () => {
        persistFromVideo(lessonKey, { reason: "pause" });
        stopSavingTimer(lessonKey);
      });

      video.addEventListener("ended", () => {
        persistFromVideo(lessonKey, { reason: "ended" });
        stopSavingTimer(lessonKey);
      });

      video.addEventListener("loadedmetadata", () => {
        persistFromVideo(lessonKey, { reason: "loadedmetadata" });
        tryResumePlayback(lessonKey);
      });
    }

    root.addEventListener("click", (event) => {
      const trigger = event.target && event.target.closest("[data-trigger]");
      if (!trigger) return;

      const lessonEl = trigger.closest(".course-landing__lesson");
      if (!lessonEl) return;

      const lessonKey = lessonEl.dataset.lessonKey;
      const st = stateByLesson.get(lessonKey);
      if (!st) return;

      window.setTimeout(() => {
        const isOpen =
          lessonEl.getAttribute("open") === "true" ||
          lessonEl.classList.contains("is-expanded") ||
          lessonEl.hasAttribute("open");

        if (!isOpen) {
          persistFromVideo(lessonKey, { reason: "closed" });
          stopSavingTimer(lessonKey);
          return;
        }

        pauseAllExcept(st.video);

        if (st.video.readyState >= 1) {
          tryResumePlayback(lessonKey);
        }
      }, 30);
    });
  }

  function initAll() {
    const roots = document.querySelectorAll(SELECTOR_ROOT);
    roots.forEach((root) => initCourseLanding(root));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target && event.target.querySelector && event.target.querySelector(SELECTOR_ROOT);
    if (root) initCourseLanding(root);
  });
})();
